import React, { useState } from 'react';
import styled from 'styled-components';
import { FolderStructure, ImageData } from '../types';

const NavigatorContainer = styled.div`
  background-color: #f0f0f0;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-height: calc(100vh - 200px);
  overflow-y: auto;
`;

const TurbineTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const BladeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const BladeItem = styled.div`
  margin-bottom: 1rem;
`;

const BladeTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
  padding: 0.5rem;
  background-color: #e0e0e0;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background-color: #d0d0d0;
  }
  
  &.active {
    background-color: #007bff;
    color: white;
  }
`;

const SurfaceList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-left: 1.5rem;
  margin-bottom: 0.5rem;
`;

const SurfaceItem = styled.div<{ isActive: boolean }>`
  padding: 0.5rem 1rem;
  background-color: ${props => props.isActive ? '#007bff' : '#e8e8e8'};
  color: ${props => props.isActive ? 'white' : 'black'};
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background-color: ${props => props.isActive ? '#0069d9' : '#d0d0d0'};
  }
`;

const ImageListContainer = styled.div`
  margin: 0.5rem 0 1rem 1.5rem;
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #fff;
`;

const ImageListItem = styled.div<{ isActive: boolean }>`
  padding: 0.5rem;
  cursor: pointer;
  border-bottom: 1px solid #eee;
  background-color: ${props => props.isActive ? '#e6f2ff' : 'transparent'};
  font-size: 0.9rem;
  
  &:hover {
    background-color: ${props => props.isActive ? '#e6f2ff' : '#f5f5f5'};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const SectionTitle = styled.div`
  font-weight: bold;
  margin: 0.5rem 0 0.5rem 1.5rem;
  font-size: 0.9rem;
  color: #555;
`;

const ShowImagesButton = styled.button`
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
  cursor: pointer;
  margin-left: 1.5rem;
  margin-bottom: 0.5rem;
  
  &:hover {
    background-color: #5a6268;
  }
`;

interface FolderNavigatorProps {
  folderStructure: FolderStructure;
  onSelectFolder: (bladeName: string, surfaceType: string) => void;
  selectedBlade: string | null;
  selectedSurface: string | null;
  images: ImageData[];
  currentImageIndex: number;
  onSelectImage: (index: number) => void;
}

const FolderNavigator: React.FC<FolderNavigatorProps> = ({
  folderStructure,
  onSelectFolder,
  selectedBlade,
  selectedSurface,
  images,
  currentImageIndex,
  onSelectImage,
}) => {
  const [showImages, setShowImages] = useState(false);
  
  // Filtrelenmiş görseller
  const filteredImages = images.filter(img => 
    img.bladeName === selectedBlade && img.surfaceType === selectedSurface
  );

  return (
    <NavigatorContainer>
      <TurbineTitle>
        Türbin: {folderStructure.turbineName}
      </TurbineTitle>
      
      <BladeList>
        {Object.keys(folderStructure.blades).map((bladeName) => (
          <BladeItem key={bladeName}>
            <BladeTitle 
              className={selectedBlade === bladeName ? 'active' : ''} 
              onClick={() => {
                if (
                  selectedBlade === bladeName && 
                  Object.keys(folderStructure.blades[bladeName].surfaces).includes(selectedSurface || '')
                ) {
                  // Zaten seçiliyse seçimi kaldır
                  onSelectFolder('', '');
                } else {
                  // İlk yüzeyi seç
                  const firstSurface = Object.keys(folderStructure.blades[bladeName].surfaces)[0];
                  onSelectFolder(bladeName, firstSurface);
                }
              }}
            >
              Kanat: {bladeName}
            </BladeTitle>
            
            {selectedBlade === bladeName && (
              <>
                <SurfaceList>
                  {Object.keys(folderStructure.blades[bladeName].surfaces).map((surfaceType) => (
                    <SurfaceItem
                      key={surfaceType}
                      isActive={selectedSurface === surfaceType}
                      onClick={() => onSelectFolder(bladeName, surfaceType)}
                    >
                      {surfaceType} ({folderStructure.blades[bladeName].surfaces[surfaceType as keyof typeof folderStructure.blades[typeof bladeName]['surfaces']].length})
                    </SurfaceItem>
                  ))}
                </SurfaceList>
                
                {selectedSurface && (
                  <>
                    <ShowImagesButton onClick={() => setShowImages(!showImages)}>
                      {showImages ? 'Görselleri Gizle' : 'Görselleri Göster'}
                    </ShowImagesButton>
                    
                    {showImages && filteredImages.length > 0 && (
                      <>
                        <SectionTitle>
                          Görseller ({filteredImages.length})
                        </SectionTitle>
                        <ImageListContainer>
                          {filteredImages.map((img, index) => (
                            <ImageListItem 
                              key={img.path} 
                              isActive={index === currentImageIndex}
                              onClick={() => onSelectImage(index)}
                            >
                              <div><strong>{index+1}.</strong> {img.fileName}</div>
                              <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                {img.fileName}
                              </div>
                            </ImageListItem>
                          ))}
                        </ImageListContainer>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </BladeItem>
        ))}
      </BladeList>
    </NavigatorContainer>
  );
};

export default FolderNavigator; 